import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { useI18n } from "@/i18n";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function Blog() {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);

  useSeo({
    title: "Insights tehnice — DMO Technology | Blog IT, Proxmox, pfSense, Securitate",
    description: "Ghiduri și articole practice despre administrare IT, virtualizare Proxmox, pfSense, backup & disaster recovery, monitorizare Zabbix și securitatea rețelei.",
    url: "https://dmo-tech.ro/blog",
  });

  useEffect(() => {
    fetch(`${API}/posts`).then((r) => r.json()).then((d) => setPosts(Array.isArray(d) ? d : [])).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="relative min-h-screen bg-ink-900 pt-28">
      <div className="pointer-events-none absolute -left-40 top-10 h-[30rem] w-[30rem] rounded-full bg-cyan-neon/8 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-neon">[ {t("blog.eyebrow")} ]</span>
        <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {t("blog.title")}
        </h1>
        <p className="mt-5 max-w-2xl font-body text-base leading-relaxed text-white/55">{t("blog.sub")}</p>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.06 }}
            >
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-ink-800 transition-colors duration-300 hover:border-cyan-neon/40">
                <div className="relative h-48 overflow-hidden">
                  <img src={p.cover} alt={p.title} className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-800 to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full border border-cyan-neon/30 bg-ink-900/80 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-cyan-neon backdrop-blur-md">{p.category}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-display text-lg font-bold leading-snug text-white transition-colors group-hover:text-cyan-neon">{p.title}</h2>
                  <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-white/55">{p.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/35"><Clock size={12} /> {p.read_min} {t("blog.read")}</span>
                    <ArrowUpRight size={16} className="text-white/40 transition-colors group-hover:text-cyan-neon" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-16">
          <Link to="/" data-testid="blog-home-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-cyan-neon">
            ← {t("blog.home")}
          </Link>
        </div>
      </div>
    </main>
  );
}
